import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input, Tabs, message, Tag } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { Table } from '../../../design-system';
import clientAPI from '../../../service/ApiServiceClientPortal';

const BRAND_BLACK = '#1B1B1B';

const styles = {
    header: {
        marginBottom: 24,
    },
    title: {
        fontSize: 22,
        fontWeight: 600,
        color: BRAND_BLACK,
        margin: 0,
    },
    searchRow: {
        marginBottom: 16,
        maxWidth: 360,
    },
};

const statusColors = {
    active: 'green',
    completed: 'blue',
    on_hold: 'orange',
    cancelled: 'red',
    draft: 'default',
};

const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
        return new Date(dateStr).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    } catch {
        return '-';
    }
};

const columns = [
    {
        title: 'Project Name',
        dataIndex: 'name',
        key: 'name',
        render: (text) => <span style={{ fontWeight: 500, color: BRAND_BLACK }}>{text}</span>,
    },
    {
        title: 'Client',
        dataIndex: 'clientName',
        key: 'clientName',
        render: (text) => text || '-',
    },
    {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (status) => {
            const label = status ? status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '-';
            return <Tag color={statusColors[status] || 'default'}>{label}</Tag>;
        },
    },
    {
        title: 'Start Date',
        dataIndex: 'startDate',
        key: 'startDate',
        render: formatDate,
    },
    {
        title: 'End Date',
        dataIndex: 'endDate',
        key: 'endDate',
        render: formatDate,
    },
];

const ClientDashboard = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('projects');
    const [projects, setProjects] = useState([]);
    const [proposals, setProposals] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [proposalPagination, setProposalPagination] = useState({ current: 1, pageSize: 10, total: 0 });

    const fetchProjects = useCallback(async (page = 1, searchTerm = '') => {
        setLoading(true);
        try {
            const params = { page, limit: 10, type: 'CONFIRMED' };
            if (searchTerm) params.search = searchTerm;
            const response = await clientAPI.get('/client-portal/projects', { params });
            const data = response.data?.data || response.data;
            setProjects(data.projects || data.items || []);
            setPagination({
                current: page,
                pageSize: 10,
                total: data.total || data.pagination?.total || 0,
            });
        } catch (err) {
            message.error('Failed to load projects');
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchProposals = useCallback(async (page = 1, searchTerm = '') => {
        setLoading(true);
        try {
            const params = { page, limit: 10, type: 'LEAD' };
            if (searchTerm) params.search = searchTerm;
            const response = await clientAPI.get('/client-portal/projects', { params });
            const data = response.data?.data || response.data;
            setProposals(data.projects || data.items || []);
            setProposalPagination({
                current: page,
                pageSize: 10,
                total: data.total || data.pagination?.total || 0,
            });
        } catch (err) {
            message.error('Failed to load proposals');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (activeTab === 'projects') {
            fetchProjects(1, search);
        } else {
            fetchProposals(1, search);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    const handleSearch = (value) => {
        setSearch(value);
        if (activeTab === 'projects') {
            fetchProjects(1, value);
        } else {
            fetchProposals(1, value);
        }
    };

    const handleTableChange = (pag) => {
        if (activeTab === 'projects') {
            fetchProjects(pag.current, search);
        } else {
            fetchProposals(pag.current, search);
        }
    };

    const onRowClick = (record) => ({
        onClick: () => navigate(`/client-section/project/${record.id}`),
        style: { cursor: 'pointer' },
    });

    const tabItems = [
        {
            key: 'projects',
            label: 'Projects',
            children: (
                <Table
                    columns={columns}
                    dataSource={projects}
                    variant="bordered"
                    rowKey="id"
                    loading={loading}
                    pagination={pagination}
                    onChange={handleTableChange}
                    onRow={onRowClick}
                />
            ),
        },
        {
            key: 'proposals',
            label: 'Proposals',
            children: (
                <Table
                    columns={columns}
                    dataSource={proposals}
                    variant="bordered"
                    rowKey="id"
                    loading={loading}
                    pagination={proposalPagination}
                    onChange={handleTableChange}
                    onRow={onRowClick}
                />
            ),
        },
    ];

    return (
        <div>
            <div style={styles.header}>
                <h1 style={styles.title}>Dashboard</h1>
            </div>

            <div style={styles.searchRow}>
                <Input
                    prefix={<SearchOutlined />}
                    placeholder="Search projects..."
                    allowClear
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                />
            </div>

            <Tabs
                activeKey={activeTab}
                onChange={(key) => {
                    setActiveTab(key);
                    setSearch('');
                }}
                items={tabItems}
            />
        </div>
    );
};

export default ClientDashboard;
